import {Text} from '@mantine/core';
import React from 'react';

import {
  LABELING_STRATEGY_CONVERT_NAME_TO_ALIAS_TEXT,
  LABELING_STRATEGY_INPUT_LABEL,
  LABELING_STRATEGY_TO_TEXT,
  LabelingStrategy,
  RegistryLabelingStrategy,
} from 'constants/RegistryConstants';
import preferences from 'Preferences';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import * as LabelingStrategiesUtil from './LabelingStrategiesUtil';

function LabelingStrategyDropdownOrMessage({
  disabled,
  labelingStrategies,
  onChange,
  selectedLabelingStrategy,
}: {
  disabled: boolean;
  labelingStrategies: Array<LabelingStrategy> | null;
  onChange: (labelingStrategy: LabelingStrategy) => void;
  selectedLabelingStrategy?: LabelingStrategy | null;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (labelingStrategies !== null && labelingStrategies.length === 0) {
    return <p className="help-block">No available options. Please select a schema.</p>;
  }
  if (
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    labelingStrategies !== null &&
    labelingStrategies.length === 1 &&
    labelingStrategies[0] === selectedLabelingStrategy
  ) {
    return <p className="help-block">{LABELING_STRATEGY_TO_TEXT[selectedLabelingStrategy]}</p>;
  }

  const displayName =
    selectedLabelingStrategy != null
      ? LABELING_STRATEGY_TO_TEXT[selectedLabelingStrategy]
      : 'Select a setting...';

  const labelingStrategyOptions = (labelingStrategies || []).map((labelingStrategy) => (
    <Option value={labelingStrategy} onSelect={onChange} key={labelingStrategy}>
      {LABELING_STRATEGY_TO_TEXT[labelingStrategy]}
    </Option>
  ));

  return (
    <SelectDropdown
      disabled={disabled}
      displayName={displayName}
      menu={
        <Menu
          scrollable={true}
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          value={selectedLabelingStrategy}
        >
          {labelingStrategyOptions}
        </Menu>
      }
    />
  );
}

function LabelingStrategyAliasHelperText({
  labelingStrategy,
}: {
  labelingStrategy?: LabelingStrategy | null;
}): JSX.Element | null {
  if (
    labelingStrategy === RegistryLabelingStrategy.DELETE_NAMES ||
    labelingStrategy === RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS
  ) {
    return (
      <Text color="dimmed" size="sm" fs="italic" mt="xs">
        {LABELING_STRATEGY_CONVERT_NAME_TO_ALIAS_TEXT}
      </Text>
    );
  }
  return null;
}

export interface LabelingStrategyInputProps {
  disabled?: boolean;
  labelingStrategies: Array<LabelingStrategy> | null;
  labelingStrategy?: LabelingStrategy | null;
  labelStyle?: string;
  onChange: (labelingStrategy: LabelingStrategy) => void;
  tagSchemaIds?: Array<string>;
}

function LabelingStrategyInput({
  tagSchemaIds,
  onChange,
  disabled = false,
  labelStyle,
  labelingStrategies,
  labelingStrategy,
}: LabelingStrategyInputProps): JSX.Element {
  const handleLabelingStrategyChange = (newLabelingStrategy: LabelingStrategy): void => {
    if (tagSchemaIds) {
      preferences.set(LabelingStrategiesUtil.getLabelingStrategyPrefKey(tagSchemaIds), newLabelingStrategy);
    }
    onChange(newLabelingStrategy);
  };

  return (
    <FormGroup>
      <FieldLabel label={LABELING_STRATEGY_INPUT_LABEL} labelStyle={labelStyle} isRequired={true} />
      <LabelingStrategyDropdownOrMessage
        disabled={Boolean(disabled)}
        labelingStrategies={labelingStrategies}
        onChange={handleLabelingStrategyChange}
        selectedLabelingStrategy={labelingStrategy}
      />
      <LabelingStrategyAliasHelperText labelingStrategy={labelingStrategy} />
    </FormGroup>
  );
}

export default LabelingStrategyInput;
